import { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Shell from "@/components/Shell";
import CurrentScriptCard from "@/components/CurrentScriptCard";
import { Sparkles, Loader2, ArrowLeft, Wand2, X } from "lucide-react";

const SCRIPT_TYPES = [
  { key: "video",  label: "Video script",       tint: "tint-blue-soft",     dot: "var(--blue-deep)",     deep: "var(--blue-deep)"     },
  { key: "email",  label: "Email / sales copy", tint: "tint-matcha-soft",   dot: "var(--matcha-deep)",   deep: "var(--matcha-deep)"   },
  { key: "social", label: "Social post",        tint: "tint-yellow-soft",   dot: "var(--yellow-deep)",   deep: "var(--yellow-deep)"   },
  { key: "sales",  label: "Long-form sales",    tint: "tint-pink-soft",     dot: "var(--pink-deep)",     deep: "var(--pink-deep)"     },
];

const MODELS = [
  { key: "claude-sonnet-4-6", label: "Claude Sonnet 4.6", note: "best for voice mimicry" },
  { key: "gpt-5.4", label: "GPT 5.4", note: "balanced" },
  { key: "gemini-3-flash", label: "Gemini 3 Flash", note: "fastest" },
];

const LENGTHS = [
  { key: "short", label: "Short" },
  { key: "medium", label: "Medium" },
  { key: "long", label: "Long" },
];

export default function GeneratePage() {
  const { clientId } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const remixId = searchParams.get("remix");
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [samplesCount, setSamplesCount] = useState(0);

  const [topic, setTopic] = useState("");
  const [notes, setNotes] = useState("");
  const [scriptType, setScriptType] = useState("video");
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [length, setLength] = useState("medium");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [remixSource, setRemixSource] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([
          api.get(`/clients/${clientId}`),
          api.get(`/clients/${clientId}/samples`),
        ]);
        setClient(c.data);
        setSamplesCount(s.data.length);
        if (remixId) {
          try {
            const { data: src } = await api.get(`/scripts/${remixId}`);
            setRemixSource(src);
            setTopic(`${src.topic} — remix`);
            setNotes(`Remix of an existing script. Keep the same voice and structure but try a fresh angle. Original brief: "${src.topic}".`);
            setScriptType(src.script_type);
            setModel(src.model);
            toast.success("Remix loaded — tweak the angle and generate");
          } catch {
            toast.error("Couldn't load source script");
          }
        }
      } catch { toast.error("Failed to load client"); }
      finally { setLoading(false); }
    })();
  }, [clientId, remixId]);

  const clearRemix = () => {
    setRemixSource(null);
    setTopic(""); setNotes("");
    setSearchParams({});
  };

  const generate = async (e) => {
    e.preventDefault();
    if (!topic.trim()) { toast.error("Give me a topic first"); return; }
    if (samplesCount === 0) { toast.error("Add at least one voice sample first"); return; }
    setGenerating(true);
    setResult(null);
    try {
      const { data } = await api.post(`/generate`, {
        client_id: clientId, script_type: scriptType, topic, extra_notes: notes, model, length,
      });
      setResult(data);
      toast.success("Script ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setGenerating(false); }
  };

  if (loading) return <Shell><div className="mono-label pulse-soft">Loading</div></Shell>;
  if (!client) return (
    <Shell>
      <div className="max-w-md">
        <h1 className="font-serif-display text-4xl">Client not found</h1>
        <Link to="/" className="mono-label mt-4 inline-block">← Back to dashboard</Link>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => nav(`/clients/${clientId}`)}
          data-testid="back-to-client"
          className="mono-label flex items-center gap-1 mb-6 hover:text-black transition-colors"
        >
          <ArrowLeft className="w-3 h-3" /> Back to {client.name}
        </button>

        <div className="mb-10">
          <div className="mono-label mb-2">{remixSource ? "Remix" : "New script for"}</div>
          <h1 className="font-serif-display text-5xl md:text-6xl tracking-tight leading-none">{client.name}</h1>
          <p className="mt-3 text-base" style={{ color: "var(--ink-2)" }}>
            {samplesCount === 0
              ? "Add at least one voice sample before generating."
              : `Writing in the voice built from ${samplesCount} sample${samplesCount !== 1 ? "s" : ""}.`}
          </p>
          {remixSource && (
            <div className="mt-5 tint-lavender-soft hair rounded-md px-4 py-3 flex items-center justify-between gap-3 rise-in" data-testid="remix-source-chip">
              <div className="flex items-center gap-2 min-w-0">
                <Wand2 className="w-4 h-4 shrink-0" style={{ color: "var(--lavender-deep)" }} />
                <div className="text-sm truncate">
                  <span className="mono-label mr-2">Remixing</span>
                  <span className="font-medium">{remixSource.topic}</span>
                </div>
              </div>
              <button
                onClick={clearRemix}
                data-testid="clear-remix"
                className="btn-ghost text-xs px-2 py-1 rounded-md inline-flex items-center gap-1 shrink-0"
              >
                <X className="w-3 h-3" /> Clear
              </button>
            </div>
          )}
        </div>

        <form onSubmit={generate} className="space-y-8 hair rounded-md p-6 md:p-8 rise-in" style={{ background: "var(--paper)" }} data-testid="generator-form">
          <div>
            <span className="mono-label block mb-3">Script type</span>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {SCRIPT_TYPES.map((t) => {
                const selected = scriptType === t.key;
                return (
                  <button
                    key={t.key} type="button"
                    data-testid={`script-type-${t.key}`}
                    onClick={() => setScriptType(t.key)}
                    className={`hair px-3 py-3 rounded-md text-sm text-left transition-colors duration-200 flex items-center gap-2 ${selected ? t.tint : ""}`}
                    style={selected ? { borderColor: t.deep, boxShadow: `inset 0 0 0 1px ${t.deep}` } : {}}
                  >
                    <span className="dot" style={{ background: t.dot }} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="block">
            <span className="mono-label block mb-2">Topic / brief</span>
            <input
              data-testid="topic-input" required
              value={topic} onChange={(e) => setTopic(e.target.value)}
              placeholder="Launch of our new 30-day sleep coaching program"
              className="w-full surface hair px-3 py-2.5 rounded-md text-base focus-primary"
            />
          </label>

          <label className="block">
            <span className="mono-label block mb-2">Extra notes (optional)</span>
            <textarea
              data-testid="notes-input"
              rows={4}
              value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Include a personal anecdote about your own sleep. End with a booking CTA."
              className="w-full surface hair px-3 py-2 rounded-md text-sm focus-primary resize-y"
            />
          </label>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <span className="mono-label block mb-2">Length</span>
              <div className="flex gap-1">
                {LENGTHS.map((l) => (
                  <button
                    key={l.key} type="button"
                    data-testid={`length-${l.key}`}
                    onClick={() => setLength(l.key)}
                    className="flex-1 hair px-3 py-2 rounded-md text-xs transition-colors duration-200"
                    style={length === l.key
                      ? { background: "var(--ink)", color: "var(--paper)", borderColor: "var(--ink)" }
                      : { background: "var(--paper)" }}
                  >{l.label}</button>
                ))}
              </div>
            </div>
            <div>
              <span className="mono-label block mb-2">Model</span>
              <select
                data-testid="model-select"
                value={model} onChange={(e) => setModel(e.target.value)}
                className="w-full surface hair px-3 py-2 rounded-md text-sm focus-primary font-mono-ui"
              >
                {MODELS.map((m) => (
                  <option key={m.key} value={m.key}>{m.label} — {m.note}</option>
                ))}
              </select>
            </div>
          </div>

          <button
            data-testid="generate-button"
            disabled={generating || samplesCount === 0}
            className="btn-primary w-full py-3 rounded-md inline-flex items-center justify-center gap-2 font-medium"
          >
            {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Ghostwriting…</> : <><Sparkles className="w-4 h-4" /> Generate script</>}
          </button>
        </form>

        {result && (
          <section className="mt-10">
            <div className="mono-label mb-4">Result</div>
            <CurrentScriptCard
              script={result}
              onUpdated={(u) => setResult(u)}
              clientName={client.name}
            />
            <div className="mt-6 flex gap-3">
              <Link
                to={`/clients/${clientId}`}
                data-testid="view-in-client"
                className="btn-ghost hair text-sm px-4 py-2 rounded-md inline-flex items-center gap-2"
                style={{ background: "var(--paper)" }}
              >
                View in {client.name}&apos;s history
              </Link>
              <button
                onClick={() => { setResult(null); setTopic(""); setNotes(""); }}
                data-testid="write-another-button"
                className="btn-primary text-sm px-4 py-2 rounded-md"
              >
                Write another
              </button>
            </div>
          </section>
        )}
      </div>
    </Shell>
  );
}
